import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  Divider,
  Button,
  Stack,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useEffect, useState, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  getNotifications,
  clearNotificationGroup,
  NotificationGroup,
} from "../../api/notifications";
import { useAppTranslation } from "../../hooks/useAppTranslation";

interface Props {
  companyId: string;
}

export default function NotificationsBell({ companyId }: Props) {
  const { t } = useAppTranslation("common");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<NotificationGroup[]>([]);
  const [loading, setLoading] = useState(false);

  const totalDocuments = notifications.reduce(
    (sum, group) => sum + group.documents.length,
    0
  );

  const [isTabActive, setIsTabActive] = useState(
    document.visibilityState === "visible"
  );

  const open = Boolean(anchorEl);
  const isCompanySelected = Boolean(companyId);

  /* ============================= */
  /* VISIBILITY HANDLER */
  /* ============================= */
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabActive(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, []);

  /* ============================= */
  /* FETCH NOTIFICATIONS */
  /* ============================= */
  const fetchNotifications = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const data = await getNotifications(companyId);
      setNotifications(data);
    } catch (err) {
      console.error("Notification fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  /* ============================= */
  /* POLLING */
  /* ============================= */
  useEffect(() => {
    if (!companyId) {
      setNotifications([]);
      return;
    }

    if (isTabActive) {
      fetchNotifications();
    }

    if (!isTabActive) return;

    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications, companyId, isTabActive]);

  /* ============================= */
  /* HANDLERS */
  /* ============================= */
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClear = async (status: string) => {
    try {
      await clearNotificationGroup(companyId, status);
      await fetchNotifications();
    } catch (err) {
      console.error("Clear notifications failed:", err);
    }
  };

  // const totalGroups = notifications.length;
  const useFixedHeight = notifications.length > 0;
  const [scrolled, setScrolled] = useState(false);

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen}>
        <Badge badgeContent={totalDocuments} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableAutoFocusItem
        PaperProps={{
          sx: {
            width: 380,
            ...(useFixedHeight && {
              height: 500,              // 🔥 stała wysokość tylko gdy są notyfikacje
              display: "flex",
              flexDirection: "column",
            }),
          },
        }}
        MenuListProps={{
          sx: {
            p: 0,
            ...(useFixedHeight && {
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }),
          },
        }}
      >
        {/* ================= HEADER ================= */}
        <Box
          px={2}
          py={1}
          sx={{
            transition: "box-shadow 0.2s ease",
            boxShadow: scrolled ? "0 2px 6px rgba(0,0,0,0.08)" : "none",
            zIndex: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {t("Powiadomienia")}
          </Typography>
        </Box>

        <Divider />

        {/* ================= SCROLLABLE LIST ================= */}
        <Box
          onScroll={(e) => {
            const target = e.currentTarget;
            setScrolled(target.scrollTop > 0);
          }}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          {loading ? (
            <Box p={2}>
              <Typography variant="body2">
                {t("Pobieranie...")}
              </Typography>
            </Box>
          ) : notifications.length === 0 ? (
              <Box textAlign="center" py={3}>
                <NotificationsIcon
                  sx={{ fontSize: 32, opacity: 0.3, mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {t("Brak nowych powiadomień")}
                </Typography>
                {!isCompanySelected && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mt={1}
                  >
                    {t("Wybierz firmę, aby przejść do ustawień")}
                  </Typography>
                )}
              </Box>
          ) : (
            notifications.map((group) => (
              <Box
                key={group.documentStatus}
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.02)",
                  },
                  transition: "background-color 0.15s ease",
                }}
              >
                <Box px={2} py={1}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="primary"
                  >
                    {t(group.documentStatusText, {}, "statuses")}
                  </Typography>

                  <Stack spacing={0.5} mt={1}>
                    {group.documents.map((doc) => (
                      <Typography
                        key={doc.documentId}
                        component={RouterLink}
                        to={`/document/${doc.documentId}?companyId=${companyId}`}
                        sx={{
                          textDecoration: "none",
                          fontSize: 14,
                          "&:hover": { textDecoration: "underline" },
                          color: "#1F2937",
                        }}
                        onClick={handleClose}
                      >
                        {doc.documentNo}
                      </Typography>
                    ))}
                  </Stack>

                  <Box mt={1}>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() =>
                        handleClear(group.documentStatus)
                      }
                    >
                      {t("Usuń")}
                    </Button>
                  </Box>
                </Box>

                <Divider />
              </Box>
            ))
          )}
        </Box>

        <Divider />

        {/* ================= FOOTER ================= */}
        <Box px={2} py={1}>
          <Button
            fullWidth
            component={RouterLink}
            to={`/settings/notifications?companyId=${companyId}`}
            onClick={handleClose}
            disabled={!isCompanySelected}
          >
            {t("Ustawienia powiadomień")}
          </Button>
        </Box>
      </Menu>
    </>
  );
}